import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { money } from '../../lib/money';
import { useNavigate } from 'react-router-dom';
import {
  Badge,
  Button,
  DataTable,
  EmptyState,
  FilterBar,
  PageContainer,
  PageHeader,
  Pagination,
  Spinner,
} from '../../ui/components';
import { Icon } from '../../ui/icons';
import { useProperty } from '../../app/PropertyContext';
import { useT } from '../../i18n';
import { stockTransferPath } from '../../lib/slug';
import { buildFilterParams } from '../../lib/filters';
import { catalogApi } from '../catalog/catalogApi';
import { transfersApi, transferStatusMeta, TRANSFER_STATUSES } from './transfersApi';
import '../requisitions/requisitions.css';

const PAGE_SIZE = 25;
export default function TransfersPage() {
  const navigate = useNavigate();
  const t = useT();
  const { activePropertyId, hasPermission } = useProperty();
  const canTransfer = hasPermission('inventory.transfer');

  const [data, setData] = useState({ items: [], totalCount: 0, page: 1, pageSize: PAGE_SIZE });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [search, setSearch] = useState('');
  const [values, setValues] = useState({});
  const [locations, setLocations] = useState([]);
  const [page, setPage] = useState(1);
  const debounceRef = useRef(null);

  useEffect(() => {
    let active = true;
    catalogApi.listLocations(activePropertyId).then((res) => { if (active) setLocations(res || []); }).catch(() => {});
    return () => { active = false; };
  }, [activePropertyId]);

  const fields = useMemo(() => [
    { id: 'statuses', label: t('Status'), type: 'multiselect', options: TRANSFER_STATUSES.map((s) => ({ value: s.value, label: t(s.label) })) },
    { id: 'locationIds', label: t('Location'), type: 'multiselect', searchable: true, options: locations.map((l) => ({ value: l.id, label: l.name })) },
    { id: 'transferred', label: t('Transfer date'), type: 'daterange', fromParam: 'transferredFrom', toParam: 'transferredTo' },
  ], [locations, t]);

  const filterParams = useMemo(() => buildFilterParams(fields, values), [fields, values]);
  const hasFilters = search !== '' || Object.keys(filterParams).length > 0;

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await transfersApi.list({ page, pageSize: PAGE_SIZE, search, ...filterParams });
      setData(res);
    } catch (err) {
      setError(err?.message || t('Failed to load transfers.'));
    } finally {
      setLoading(false);
    }
  }, [page, search, filterParams, t]);

  useEffect(() => { load(); }, [load]);

  const onSearchChange = (value) => {
    setSearchText(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => { setPage(1); setSearch(value); }, 300);
  };

  const onFilterChange = (id, value) => {
    setPage(1);
    setValues((prev) => {
      const next = { ...prev };
      if (value === undefined) delete next[id];
      else next[id] = value;
      return next;
    });
  };

  const locationName = (lid) => locations.find((l) => l.id === lid)?.name || '—';
  const open = (row) => navigate(stockTransferPath(row));

  const columns = [
    { key: 'number', header: t('Transfer'), width: 130, render: (tr) => <span style={{ fontFamily: 'var(--font-mono)' }}>{tr.number || t('Draft')}</span> },
    {
      key: 'route', header: t('From → To'), width: 280,
      render: (tr) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
          <span>{locationName(tr.fromLocationId)}</span>
          <Icon.ArrowRight size={14} className="pp-cell-muted" />
          <span>{locationName(tr.toLocationId)}</span>
        </div>
      ),
    },
    {
      key: 'status', header: t('Status'), width: 110,
      render: (tr) => {
        const m = transferStatusMeta(tr.status);
        return <Badge tone={m.tone} dot>{t(m.label)}</Badge>;
      },
    },
    { key: 'date', header: t('Transfer date'), width: 120, render: (tr) => (tr.transferDate ? new Date(tr.transferDate).toLocaleDateString() : '—') },
    { key: 'lines', header: t('Items'), width: 80, align: 'right', render: (tr) => tr.lineCount },
    { key: 'total', header: t('Value'), width: 140, align: 'right', render: (tr) => money(tr.total) },
  ];

  const totalPages = Math.max(1, Math.ceil(data.totalCount / PAGE_SIZE));

  return (
    <PageContainer size="full">
      <PageHeader
        title={t('Transfers')}
        description={t("Move stock between locations. Posting a transfer carries the value at the source's moving-average cost and updates on-hand at both ends.")}
        icon={<Icon.ArrowLeftRight size={22} />}
        actions={canTransfer ? (
          <Button variant="primary" leadingIcon={<Icon.Plus size={16} />} onClick={() => navigate('/transfers/new')}>
            {t('New transfer')}
          </Button>
        ) : null}
      />

      <FilterBar
        searchPlaceholder={t('Search by transfer number or notes…')}
        searchValue={searchText}
        onSearchChange={onSearchChange}
        fields={fields}
        values={values}
        onChange={onFilterChange}
      />

      {error ? (
        <EmptyState
          icon={<Icon.CircleAlert size={28} />}
          title={t("Couldn't load transfers")}
          description={error}
          action={<Button variant="secondary" onClick={load}>{t('Retry')}</Button>}
        />
      ) : loading ? (
        <div style={{ display: 'grid', placeItems: 'center', padding: 'var(--space-16)' }}><Spinner size={26} /></div>
      ) : (
        <>
          <DataTable
            columns={columns}
            data={data.items}
            rowKey={(tr) => tr.id}
            density="comfortable"
            onRowClick={open}
            emptyState={
              <EmptyState
                icon={<Icon.ArrowLeftRight size={28} />}
                title={hasFilters ? t('No matching transfers') : t('No transfers yet')}
                description={hasFilters ? t('Try adjusting your search or filters.') : t('Move stock between two locations — the value follows at moving-average cost.')}
                action={canTransfer && !hasFilters ? <Button variant="secondary" onClick={() => navigate('/transfers/new')}>{t('New transfer')}</Button> : null}
              />
            }
          />
          {data.totalCount > 0 && (
            <div style={{ marginTop: 'var(--space-4)' }}>
              <Pagination page={page} pageCount={totalPages} onPageChange={setPage} totalItems={data.totalCount} pageSize={PAGE_SIZE} />
            </div>
          )}
        </>
      )}
    </PageContainer>
  );
}
